import "./Graficos.css";
import React, { useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { Bar, Line, Pie, Radar } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  RadarController,
  RadialLinearScale,
  Tooltip,
} from "chart.js";
import {
  FaCertificate,
  FaChartBar,
  FaCheckCircle,
  FaClock,
  FaExchangeAlt,
  FaEye,
  FaHardHat,
  FaInstagram,
  FaMapMarkedAlt,
  FaRegNewspaper,
} from "react-icons/fa";
import CargandoSpinner from "../../0. Utilidades/loadingSpinner";
import { getApiUrl } from "../../../utils/apiUrl";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  RadarController,
  RadialLinearScale,
  Tooltip,
);

const COLORS = {
  purple: "#6741B5",
  purpleSoft: "rgba(103, 65, 181, 0.18)",
  blue: "#278BB5",
  blueSoft: "rgba(39, 139, 181, 0.18)",
  coral: "#D95C68",
  gold: "#D6A327",
  green: "#2F9B69",
  red: "#D95C68",
  teal: "#2D9DA6",
  orange: "#E58A3A",
  grid: "rgba(73, 57, 94, 0.1)",
  text: "#544A61",
};

const PIE_COLORS = [
  COLORS.purple,
  COLORS.blue,
  COLORS.green,
  COLORS.gold,
  COLORS.coral,
  COLORS.teal,
  COLORS.orange,
  "#8E6BAD",
];

const compactCategories = (
  items,
  maximum = 6,
  remainderLabel = "Otras",
  sortByValue = true,
) => {
  const ordered = sortByValue
    ? [...items].sort((first, second) => second.value - first.value)
    : [...items];
  if (ordered.length <= maximum) return ordered;

  const visible = ordered.slice(0, maximum);
  const remainder = ordered
    .slice(maximum)
    .reduce((total, item) => total + item.value, 0);

  return [...visible, { label: remainderLabel, value: remainder }];
};

const truncateLabel = (label, maximum = 24) =>
  label.length > maximum ? `${label.slice(0, maximum - 1)}…` : label;

const horizontalOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 450 },
  plugins: {
    legend: { display: false },
    tooltip: {
      displayColors: false,
      callbacks: {
        title: (items) => items[0]?.label || "",
        label: (item) => `${item.raw} maestras`,
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: COLORS.grid },
      ticks: { color: COLORS.text, precision: 0, maxTicksLimit: 5 },
    },
    y: {
      grid: { display: false },
      ticks: {
        color: COLORS.text,
        font: { size: 10, weight: "600" },
        callback(value) {
          return truncateLabel(this.getLabelForValue(value));
        },
      },
    },
  },
};

const trendOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "index" },
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        boxWidth: 9,
        boxHeight: 9,
        color: COLORS.text,
        font: { size: 10, weight: "600" },
        padding: 12,
        usePointStyle: true,
      },
    },
    tooltip: { displayColors: false },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: COLORS.text, autoSkip: false, maxRotation: 45 },
    },
    y: {
      beginAtZero: true,
      grid: { color: COLORS.grid },
      ticks: { color: COLORS.text, precision: 0, maxTicksLimit: 5 },
    },
  },
};

const singleTrendOptions = {
  ...trendOptions,
  plugins: {
    ...trendOptions.plugins,
    legend: { display: false },
  },
};

const percentageOptions = {
  ...singleTrendOptions,
  plugins: {
    ...singleTrendOptions.plugins,
    tooltip: {
      displayColors: false,
      callbacks: { label: (item) => `${item.raw}%` },
    },
  },
  scales: {
    ...singleTrendOptions.scales,
    y: {
      beginAtZero: true,
      suggestedMax: 100,
      max: 100,
      grid: { color: COLORS.grid },
      ticks: {
        color: COLORS.text,
        callback: (value) => `${value}%`,
      },
    },
  },
};

const newsOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "index" },
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        boxWidth: 9,
        boxHeight: 9,
        color: COLORS.text,
        font: { size: 10, weight: "600" },
        padding: 12,
        usePointStyle: true,
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: {
        color: COLORS.text,
        maxRotation: 0,
        maxTicksLimit: 6,
        font: { size: 9 },
      },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      grid: { color: COLORS.grid },
      ticks: { color: COLORS.text, precision: 0, maxTicksLimit: 5 },
    },
  },
};

const createNewsTooltipPlaceholder = () => {
  const placeholder = document.createElement("span");
  placeholder.className = "statistics-news-tooltip-placeholder";
  placeholder.setAttribute("aria-hidden", "true");
  return placeholder;
};

const topViewedTooltip = ({ chart, tooltip }) => {
  const container = chart.canvas.parentNode;
  let element = container.querySelector(".statistics-news-tooltip");

  if (!element) {
    element = document.createElement("div");
    element.className = "statistics-news-tooltip";
    element.setAttribute("role", "tooltip");
    container.appendChild(element);
  }

  if (!tooltip || tooltip.opacity === 0) {
    element.style.opacity = 0;
    return;
  }

  const dataPoint = tooltip.dataPoints?.[0];
  const publication = dataPoint?.dataset?.publications?.[dataPoint.dataIndex];
  if (!publication) {
    element.style.opacity = 0;
    return;
  }

  element.replaceChildren();
  if (publication.thumbnail) {
    const image = document.createElement("img");
    image.src = publication.thumbnail;
    image.alt = "";
    image.onerror = () => {
      if (publication.image && image.src !== publication.image) {
        image.onerror = () => image.replaceWith(createNewsTooltipPlaceholder());
        image.src = publication.image;
        return;
      }
      image.replaceWith(createNewsTooltipPlaceholder());
    };
    element.appendChild(image);
  } else {
    element.appendChild(createNewsTooltipPlaceholder());
  }

  const copy = document.createElement("span");
  const title = document.createElement("strong");
  title.textContent = publication.title;
  const detail = document.createElement("small");
  detail.textContent = `${publication.type} · ${publication.value} visualizaciones`;
  copy.append(title, detail);
  element.appendChild(copy);

  element.style.opacity = 1;
  element.style.left = `${tooltip.caretX}px`;
  element.style.top = `${tooltip.caretY}px`;
};

const topViewedOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "nearest", axis: "y" },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false, external: topViewedTooltip },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: COLORS.grid },
      ticks: { color: COLORS.text, precision: 0, maxTicksLimit: 5 },
    },
    y: {
      grid: { display: false },
      ticks: {
        color: COLORS.text,
        font: { size: 10, weight: "600" },
        callback(value) {
          return truncateLabel(this.getLabelForValue(value), 25);
        },
      },
    },
  },
};

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        boxWidth: 9,
        boxHeight: 9,
        color: COLORS.text,
        font: { size: 10, weight: "600" },
        padding: 10,
        usePointStyle: true,
      },
    },
    tooltip: {
      callbacks: { label: (item) => `${item.label}: ${item.raw} maestras` },
    },
  },
};

const hoursOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "index" },
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        boxWidth: 9,
        boxHeight: 9,
        color: COLORS.text,
        font: { size: 10, weight: "600" },
        padding: 12,
        usePointStyle: true,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: COLORS.text, autoSkip: false, maxRotation: 45 },
    },
    y: {
      beginAtZero: true,
      grid: { color: COLORS.grid },
      ticks: { color: COLORS.text, precision: 0, maxTicksLimit: 5 },
    },
  },
};

const verticalCategoryOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      displayColors: false,
      callbacks: { label: (item) => `${item.raw} maestras` },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        color: COLORS.text,
        autoSkip: false,
        maxRotation: 45,
        minRotation: 20,
        font: { size: 9, weight: "600" },
        callback(value) {
          return truncateLabel(this.getLabelForValue(value), 15);
        },
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: COLORS.grid },
      ticks: { color: COLORS.text, precision: 0, maxTicksLimit: 5 },
    },
  },
};

const radarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      displayColors: false,
      callbacks: { label: (item) => `${item.raw} maestras SEC` },
    },
  },
  scales: {
    r: {
      beginAtZero: true,
      grid: { color: COLORS.grid },
      angleLines: { color: COLORS.grid },
      ticks: { display: false, precision: 0 },
      pointLabels: {
        color: COLORS.text,
        font: { size: 9, weight: "600" },
        callback: (label) => (
          Array.isArray(label)
            ? [truncateLabel(label[0], 15), label[1]]
            : truncateLabel(label, 15)
        ),
      },
    },
  },
};

const ChartCard = ({ icon, title, caption, action, note, children, empty }) => (
  <article className="statistics-chart-card">
    <header>
      <span className="statistics-chart-icon" aria-hidden="true">{icon}</span>
      <div>
        <h4>{title}</h4>
        <p>{caption}</p>
      </div>
      {action && <div className="statistics-chart-action">{action}</div>}
    </header>
    <div className="statistics-chart-canvas">
      {empty ? <p className="statistics-empty">Todavía no hay datos para mostrar.</p> : children}
    </div>
    {note && <p className="statistics-chart-note">{note}</p>}
  </article>
);

const Graficos = ({ topic = "personas" }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [initiativeMode, setInitiativeMode] = useState("total");
  const [hoursMode, setHoursMode] = useState("total");

  useEffect(() => {
    const controller = new AbortController();

    const loadStatistics = async () => {
      try {
        const response = await fetch(getApiUrl("api/graficos/"), {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        setStatistics(await response.json());
      } catch (requestError) {
        if (requestError.name === "AbortError") return;
        console.error("No se pudieron cargar las estadísticas:", requestError);
        setError("No se pudieron cargar las estadísticas.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadStatistics();
    return () => controller.abort();
  }, []);

  const regionItems = useMemo(
    () => compactCategories(statistics?.region_data || [], 6, "Otras regiones"),
    [statistics],
  );
  const tradeItems = useMemo(
    () => compactCategories(statistics?.oficio_data || [], 6, "Otros oficios"),
    [statistics],
  );
  const secTradeItems = useMemo(
    () => compactCategories(
      statistics?.sec_oficio_data || [],
      6,
      "Otros oficios",
      false,
    ),
    [statistics],
  );
  const statusItems = statistics?.estado_data || [];
  const initiativeItems = statistics?.initiative_data || [];
  const initiativeHoursItems = statistics?.initiative_hours_data || [];
  const approvalItems = statistics?.approval_data || [];
  const newsItems = statistics?.news_data || [];
  const topViewedNewsItems = statistics?.top_viewed_news_data || [];
  const instagramItems = statistics?.instagram_post_data || [];
  const instagramInteractionItems = statistics?.instagram_interaction_data || [];

  const horizontalData = (items, color, softColor) => ({
    labels: items.map((item) => item.label),
    datasets: [{
      data: items.map((item) => item.value),
      backgroundColor: softColor,
      borderColor: color,
      borderWidth: 1.5,
      borderRadius: 7,
      borderSkipped: false,
      barThickness: 14,
    }],
  });

  const verticalData = (items, color, softColor) => ({
    labels: items.map((item) => item.label),
    datasets: [{
      data: items.map((item) => item.value),
      backgroundColor: softColor,
      borderColor: color,
      borderWidth: 1.5,
      borderRadius: 7,
      borderSkipped: false,
      maxBarThickness: 30,
    }],
  });

  const radarData = (items) => ({
    labels: items.map((item) => [item.label, String(item.value)]),
    datasets: [{
      data: items.map((item) => item.value),
      backgroundColor: "rgba(47, 155, 105, 0.2)",
      borderColor: COLORS.green,
      borderWidth: 2,
      pointBackgroundColor: "#FFFFFF",
      pointBorderColor: COLORS.green,
      pointBorderWidth: 2,
      pointRadius: 3,
    }],
  });

  const initiativeTotalData = {
    labels: initiativeItems.map((item) => item.label),
    datasets: [{
      label: "Total",
      data: initiativeItems.map((item) => item.total),
      borderColor: COLORS.purple,
      backgroundColor: COLORS.purpleSoft,
      fill: true,
      tension: 0.35,
      pointBackgroundColor: "#FFFFFF",
      pointBorderColor: COLORS.purple,
      pointBorderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
    }],
  };

  const initiativeSplitData = {
    labels: initiativeItems.map((item) => item.label),
    datasets: [
      {
        label: "Programas",
        data: initiativeItems.map((item) => item.programas),
        borderColor: COLORS.purple,
        backgroundColor: COLORS.purpleSoft,
        tension: 0.35,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: COLORS.purple,
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: "Iniciativas",
        data: initiativeItems.map((item) => item.iniciativas),
        borderColor: COLORS.blue,
        backgroundColor: COLORS.blueSoft,
        tension: 0.35,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: COLORS.blue,
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  const pieData = (items, colors = PIE_COLORS) => ({
    labels: items.map((item) => item.label),
    datasets: [{
      data: items.map((item) => item.value),
      backgroundColor: items.map((_item, index) => colors[index % colors.length]),
      borderColor: "#FFFFFF",
      borderWidth: 2,
      hoverOffset: 5,
    }],
  });

  const initiativeHoursTotalData = {
    labels: initiativeHoursItems.map((item) => item.label),
    datasets: [{
      label: "Horas totales",
      data: initiativeHoursItems.map((item) => item.total),
      backgroundColor: COLORS.purple,
      borderRadius: 5,
      borderSkipped: false,
    }],
  };

  const initiativeHoursSplitData = {
    labels: initiativeHoursItems.map((item) => item.label),
    datasets: [
      {
        label: "Capacitación",
        data: initiativeHoursItems.map((item) => item.capacitacion),
        backgroundColor: COLORS.purple,
        borderRadius: 5,
      },
      {
        label: "Talleres",
        data: initiativeHoursItems.map((item) => item.talleres),
        backgroundColor: COLORS.blue,
        borderRadius: 5,
      },
      {
        label: "Extras",
        data: initiativeHoursItems.map((item) => item.extras),
        backgroundColor: COLORS.gold,
        borderRadius: 5,
      },
    ],
  };

  const approvalData = (category, color, softColor) => {
    const availableItems = approvalItems.filter((item) => item[category] !== null);
    return {
      labels: availableItems.map((item) => item.label),
      datasets: [{
        data: availableItems.map((item) => item[category]),
        borderColor: color,
        backgroundColor: softColor,
        fill: true,
        tension: 0.35,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: color,
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
      }],
    };
  };

  const monthFormatter = new Intl.DateTimeFormat("es-CL", {
    month: "short",
    year: "2-digit",
  });
  const newsData = {
    labels: newsItems.map((item) => {
      const [year, month] = item.label.split("-").map(Number);
      return monthFormatter.format(new Date(year, month - 1, 1)).replace(".", "");
    }),
    datasets: [
      {
        label: "Noticias",
        data: newsItems.map((item) => item.noticias),
        backgroundColor: COLORS.purple,
        borderRadius: 5,
        borderSkipped: false,
      },
      {
        label: "Testimonios",
        data: newsItems.map((item) => item.testimonios),
        backgroundColor: COLORS.gold,
        borderRadius: 5,
        borderSkipped: false,
      },
    ],
  };

  const topViewedNewsData = {
    labels: topViewedNewsItems.map((item) => item.title),
    datasets: [{
      data: topViewedNewsItems.map((item) => item.value),
      publications: topViewedNewsItems,
      backgroundColor: topViewedNewsItems.map((item) => (
        item.type === "Noticia" ? COLORS.blueSoft : "rgba(214, 163, 39, 0.2)"
      )),
      borderColor: topViewedNewsItems.map((item) => (
        item.type === "Noticia" ? COLORS.blue : COLORS.gold
      )),
      borderWidth: 1.5,
      borderRadius: 7,
      borderSkipped: false,
      barThickness: 18,
    }],
  };

  const instagramData = {
    labels: instagramItems.map((item) => {
      const [year, month] = item.label.split("-").map(Number);
      return monthFormatter.format(new Date(year, month - 1, 1)).replace(".", "");
    }),
    datasets: [{
      label: "Publicaciones",
      data: instagramItems.map((item) => item.value),
      backgroundColor: COLORS.coral,
      borderRadius: 5,
      borderSkipped: false,
    }],
  };

  const instagramInteractionLabels = instagramInteractionItems.map((item) => {
    const [year, month] = item.label.split("-").map(Number);
    return monthFormatter.format(new Date(year, month - 1, 1)).replace(".", "");
  });

  const instagramInteractionData = {
    labels: instagramInteractionLabels,
    datasets: [{
      label: "Interacciones totales",
      data: instagramInteractionItems.map((item) => item.value),
      backgroundColor: COLORS.purple,
      borderRadius: 5,
      borderSkipped: false,
    }],
  };

  if (loading) {
    return <div className="statistics-loading"><CargandoSpinner /></div>;
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div id="graficos" aria-label={`Estadísticas de ${topic}`}>
      <div className="statistics-grid">
        {topic === "personas" && <>
          <ChartCard icon={<FaMapMarkedAlt />} title="Maestras por Región" caption="Regiones con mayor presencia activa" empty={!regionItems.length}>
            <Bar data={horizontalData(regionItems, COLORS.purple, COLORS.purpleSoft)} options={horizontalOptions} />
          </ChartCard>
          <ChartCard
            icon={<FaHardHat />}
            title="Maestras asociadas por Oficio"
            caption="Comparación independiente entre oficios"
            note="Una Maestra puede pertenecer a varios oficios. Cada barra es válida por separado; no sumes las barras para obtener un total."
            empty={!tradeItems.length}
          >
            <Bar data={verticalData(tradeItems, COLORS.blue, COLORS.blueSoft)} options={verticalCategoryOptions} />
          </ChartCard>
          <ChartCard
            icon={<FaCertificate />}
            title="Maestras SEC asociadas por Oficio"
            caption="Comparación independiente entre oficios"
            note="Una Maestra SEC puede pertenecer a varios oficios. Lee cada eje por separado; no sumes los valores para obtener un total."
            empty={!secTradeItems.length}
          >
            <Radar data={radarData(secTradeItems)} options={radarOptions} />
          </ChartCard>
          <ChartCard icon={<FaCheckCircle />} title="Maestras por Estado" caption="Seguimiento OK y con Problema" empty={!statusItems.some((item) => item.value)}>
            <Pie data={pieData(statusItems, [COLORS.green, COLORS.red])} options={pieOptions} />
          </ChartCard>
        </>}

        {topic === "iniciativas" && <>
          <ChartCard
            icon={<FaChartBar />}
            title="Iniciativas por Año"
            caption={initiativeMode === "total" ? "Programas y otras Iniciativas como un solo total" : "Programas y otras Iniciativas en líneas separadas"}
            action={<button type="button" className="statistics-chart-toggle" onClick={() => setInitiativeMode((current) => current === "total" ? "split" : "total")} aria-label={initiativeMode === "total" ? "Separar Programas e Iniciativas" : "Mostrar total de Iniciativas"} title={initiativeMode === "total" ? "Separar Programas e Iniciativas" : "Mostrar total"} aria-pressed={initiativeMode === "split"}><FaExchangeAlt /></button>}
            empty={!initiativeItems.some((item) => item.total)}
          >
            <Line key={initiativeMode} data={initiativeMode === "total" ? initiativeTotalData : initiativeSplitData} options={initiativeMode === "total" ? singleTrendOptions : trendOptions} />
          </ChartCard>
          <ChartCard
            icon={<FaClock />}
            title="Horas por Año"
            caption={hoursMode === "total" ? "Suma de los tres campos de horas" : "Capacitación, talleres y horas extras"}
            action={<button type="button" className="statistics-chart-toggle" onClick={() => setHoursMode((current) => current === "total" ? "split" : "total")} aria-label={hoursMode === "total" ? "Separar por tipo de hora" : "Mostrar horas totales"} title={hoursMode === "total" ? "Separar por tipo de hora" : "Mostrar total"} aria-pressed={hoursMode === "split"}><FaExchangeAlt /></button>}
            empty={!initiativeHoursItems.some((item) => item.total)}
          >
            <Bar key={hoursMode} data={hoursMode === "total" ? initiativeHoursTotalData : initiativeHoursSplitData} options={hoursMode === "total" ? singleTrendOptions : hoursOptions} />
          </ChartCard>
          <ChartCard icon={<FaCheckCircle />} title="Aprobación de Programas por Año" caption="Promedio anual de aprobación" empty={!approvalItems.some((item) => item.programas !== null)}>
            <Line data={approvalData("programas", COLORS.purple, COLORS.purpleSoft)} options={percentageOptions} />
          </ChartCard>
          <ChartCard icon={<FaCheckCircle />} title="Aprobación de Iniciativas por Año" caption="Promedio anual de aprobación, sin Programas" empty={!approvalItems.some((item) => item.iniciativas !== null)}>
            <Line data={approvalData("iniciativas", COLORS.blue, COLORS.blueSoft)} options={percentageOptions} />
          </ChartCard>
        </>}

        {topic === "novedades" && <>
          <ChartCard icon={<FaRegNewspaper />} title="Novedades últimos 12 meses" caption="Noticias y testimonios visibles publicados por mes" empty={!newsItems.some((item) => item.noticias || item.testimonios)}>
            <Bar data={newsData} options={newsOptions} />
          </ChartCard>
          <ChartCard icon={<FaEye />} title="Novedades más vistas" caption="Top 5 por visualizaciones acumuladas" empty={!topViewedNewsItems.length}>
            <Bar data={topViewedNewsData} options={topViewedOptions} />
          </ChartCard>
          <ChartCard icon={<FaInstagram />} title="Publicaciones de Instagram" caption="Cantidad mensual de los últimos 12 meses" empty={!instagramItems.some((item) => item.value)}>
            <Bar data={instagramData} options={newsOptions} />
          </ChartCard>
          <ChartCard
            icon={<FaInstagram />}
            title="Interacciones en Instagram"
            caption="Total mensual de los últimos 3 meses"
            empty={!instagramInteractionItems.some((item) => item.value)}
          >
            <Bar data={instagramInteractionData} options={newsOptions} />
          </ChartCard>
        </>}
      </div>
    </div>
  );
};

export default Graficos;
